import { MassActionData } from '@components/MassActions/contexts/MassActions.context';
import { InfinityListManagerWithSelection } from '../../../components/Hooks/infinity-list/infinityList.types';
import { computeParentToggleSelection } from '../../../components/Hooks/infinity-list/infinityList.utils';
import { WithId } from '../../../types/Base.types';
import { useInfiniteListWithCheckbox } from '../useInfiniteListWithCheckbox';
import { ToListWithCountParams } from './useInfiniteListWithCheckbox.transformers.types';

interface DefaultPageData extends WithId {
  [key: string]: any;
}

export class UseInfiniteListWithCheckboxTransformers {
  static toMassActionData({
    query,
    checkboxManager,
  }: Omit<
    ReturnType<typeof useInfiniteListWithCheckbox<DefaultPageData>>,
    'mutateMethods'
  >): MassActionData {
    return {
      listOrDefault: query.pagesData || [],
      isAllItemsSelected: checkboxManager.selectedItems.size === query.pagesData?.length,
      list: {
        count: query.countData?.virtualTotal || query.countData?.total,
        selecteds: [...checkboxManager.selectedItems],
        changeSelecteds: (items) => checkboxManager.clearAndSelectItems(new Set(items)),
      },
      listActions: {
        toggleAllSelected: () =>
          checkboxManager.addAllIfNoneSelectedOrClearAll(
            new Set(query.pagesData?.map((pageData) => pageData.id)),
          ),
      },
    };
  }

  static toInfinityListManager<T extends DefaultPageData>({
    query,
    mutateMethods,
    checkboxManager,
  }: ReturnType<typeof useInfiniteListWithCheckbox<T>>): InfinityListManagerWithSelection<T> {
    const selectedItems = (query.pagesData ?? []).filter((item) =>
      checkboxManager.selectedItems.has(item.id),
    );

    return {
      reload: query.refetch,
      removeItem: mutateMethods.removeByIndex,
      updateItem: mutateMethods.mutateByIndex,
      replaceAllItems: mutateMethods.replaceAllItems,
      reset: () => {
        query.refetch();
        checkboxManager.reset();
      },
      loadMoreRows: async () => {
        if (query.hasNextPage && !query.isFetchingNextPage) {
          await query.fetchNextPage();
        }
      },
      checkbox: {
        toggleSelect: checkboxManager.toggleItem,
        toggleSelectBatch: (keys) => checkboxManager.clearAndSelectItems(new Set(keys)),
        toggleSelectParent: (childIds) => {
          checkboxManager.clearAndSelectItems(
            new Set(
              computeParentToggleSelection({
                currentSelected: [...checkboxManager.selectedItems],
                childIds,
              }),
            ),
          );
        },
        isAllItemsSelected: checkboxManager.selectedItems.size === query.pagesData?.length,
        toggleSelectAll: () => {
          checkboxManager.addAllIfNoneSelectedOrClearAll(
            new Set(query.pagesData?.map((pageData) => pageData.id)),
          );
        },
        toggleSelectAllWithParents: () => {
          const allIdsWithParents =
            query.pagesData?.flatMap((pageData) => {
              if (pageData.isParent) {
                const parentIds =
                  pageData.subRows?.map((subRow: DefaultPageData) => subRow.id) || [];
                return parentIds;
              }

              return pageData.id;
            }) || [];

          checkboxManager.addAllIfNoneSelectedOrClearAll(new Set(allIdsWithParents));
        },
      },
      state: {
        isLoadingPage: query.isLoading,
        hasMore: !!query.hasNextPage,
        isLoadingMore: query.isFetchingNextPage,
        totalItems: query.countData?.total || 0,
        items: query.isError ? [] : query.pagesData || [],
        page: query.pageParams ? query.pageParams.length : 0,
        selecteds: Array.from(selectedItems.map((item) => item.id)),
        selectedItems: checkboxManager.selectedItems.size ? selectedItems : [],
      },
    };
  }

  static toListWithCount<T extends DefaultPageData>({
    infiniteListWithCheckbox,
    countQuery,
  }: ToListWithCountParams<T>) {
    const baseList =
      UseInfiniteListWithCheckboxTransformers.toInfinityListManager<T>(infiniteListWithCheckbox);
    const baseListData =
      UseInfiniteListWithCheckboxTransformers.toMassActionData(infiniteListWithCheckbox);

    const list: InfinityListManagerWithSelection<T> = {
      ...baseList,
      reload: () => {
        baseList.reload();
        countQuery.refetch();
      },
      reset: () => {
        baseList.reset();
        countQuery.refetch();
      },
      state: { ...baseList.state, totalItems: countQuery.data ?? 0 },
    };

    const listData: MassActionData = {
      ...baseListData,
      list: { ...baseListData.list, count: countQuery.data },
    };

    return { list, listData, total: countQuery.data };
  }
}
